/**
 * useRecordingInputs (GDN-2510)
 *
 * Durable recording evidence for an encounter: classification (dictation-only
 * vs full) and the true recording window, both sourced from the persisted
 * recording documents on `encounter.inputs` — evidence that survives session
 * cleanup, refetches, and page reloads.
 *
 * Why fetch the documents at all: the encounter's `inputs[]` refs come back
 * from the server WITHOUT titles (the backend appends refs from
 * id/type/created_at only), so ref titles alone cannot classify a recording
 * after a refetch. The full document carries both the title
 * ("Encounter Recording" / "Dictation N") and the elapsed seconds
 * (`description`). Fetches go through the same RTK Query endpoint the
 * dictation cards use, so on the recording screen they are cache hits.
 */

import { useEffect, useMemo, useState } from 'react';
import type { DocumentResponse } from '@store/generated/encounters';
import { useAppDispatch } from '@store/hooks';

import { encountersApi } from '../store/api';
import {
  buildRecordingSignals,
  classifyEncounterRecordings,
  deriveRecordingWindowFromDocs,
  type EncounterRecordingClass,
  isRecordingRef,
  isTempRecordingRef,
  type RecordingRefLike,
} from '../utils/recordingClassification';

export interface RecordingWindowValue {
  start: Date;
  end: Date | null;
}

export interface RecordingInputsResult {
  /** Durable classification of the encounter's recordings. */
  classification: EncounterRecordingClass;
  /**
   * True only when every recording is CONFIRMED to be a dictation. 'unknown'
   * is deliberately false — misclassifying a full recording as a dictation
   * (amber ask, "Time of dictation" labels, scheduled auto-default) is the
   * harmful direction.
   */
  isDictationOnly: boolean;
  /** True when a full recording is confirmed on the encounter. */
  hasFullRecording: boolean;
  /**
   * The earliest recording's window derived from its document:
   * end = document created_at (written at recording stop), start = end −
   * elapsed seconds. When elapsed is unknown, start = created_at with no end.
   * Null until a non-temp recording document with a created_at is available.
   */
  docRecordingWindow: RecordingWindowValue | null;
}

// The app tsconfig types dispatch without the injected-endpoint thunk
// overload, so assert the documented RTK subscription shape (same pattern as
// useMedicalRecordsExport).
interface QuerySubscription<T> {
  unwrap: () => Promise<T>;
  unsubscribe: () => void;
}

export const useRecordingInputs = (
  encounterId: string | null | undefined,
  inputs: RecordingRefLike[] | null | undefined,
): RecordingInputsResult => {
  const dispatch = useAppDispatch();
  const [docsById, setDocsById] = useState<Record<string, DocumentResponse>>({});

  const recordingRefs = useMemo(
    () => (inputs ?? []).filter(isRecordingRef),
    [inputs],
  );

  // Stable key so the fetch effect reruns only when the real document set changes.
  const realDocIdsKey = useMemo(
    () =>
      recordingRefs
        .filter(ref => !isTempRecordingRef(ref))
        .map(ref => ref.encounter_document_id)
        .sort()
        .join(','),
    [recordingRefs],
  );

  useEffect(() => {
    if (!encounterId || !realDocIdsKey) return undefined;
    const docIds = realDocIdsKey.split(',');
    const subscriptions = docIds.map(
      documentId =>
        dispatch(
          encountersApi.endpoints.getV2ProviderEncountersByEncounterIdDocumentsAndDocumentId.initiate(
            { encounterId, documentId },
          ),
        ) as unknown as QuerySubscription<DocumentResponse>,
    );
    let cancelled = false;
    void Promise.all(
      subscriptions.map(sub => sub.unwrap().catch(() => null)),
    ).then(docs => {
      if (cancelled) return;
      const next: Record<string, DocumentResponse> = {};
      docs.forEach(doc => {
        if (doc) next[doc.encounter_document_id] = doc;
      });
      setDocsById(next);
    });
    return () => {
      cancelled = true;
      subscriptions.forEach(sub => sub.unsubscribe());
    };
  }, [dispatch, encounterId, realDocIdsKey]);

  return useMemo(() => {
    const classification = classifyEncounterRecordings(
      buildRecordingSignals(recordingRefs, docsById),
    );
    return {
      classification,
      isDictationOnly: classification === 'dictation_only',
      hasFullRecording: classification === 'full',
      docRecordingWindow: deriveRecordingWindowFromDocs(recordingRefs, docsById),
    };
  }, [recordingRefs, docsById]);
};
